import { Side } from '@floating-ui/react'
import { ReactNode } from 'react'

import { KeyboardShortcut } from '../../utils/keyboardShortcuts'

type Props = {
    children: ReactNode
    /** Plain text only, a string or a number. Anything with structure belongs in an AstraHoverCard. */
    tooltipContent?: string | number | null
    /** A keyboard shortcut for the trigger's action, shown as key caps after the text. */
    shortcut?: KeyboardShortcut
    /** Defaults to true. */
    showArrow?: boolean
    /** Render the child alone, with no tooltip. Same effect as an empty `tooltipContent`. */
    hidden?: boolean
    /**
     * `top`, `right`, `bottom` or `left`; defaults to `top`. Only `top` flips, to `bottom`, when the panel
     * doesn't fit. The other three keep their side and get clamped to the viewport, with the arrow moving to
     * keep pointing at the trigger.
     */
    side?: Side
    /** Keep the string on one line, letting it overflow rather than wrap. */
    noWrapText?: boolean
    /**
     * Turn off for dense grids where the pointer sweeps across many tooltips on its way somewhere else,
     * so each one waits the full delay instead of popping open as the pointer passes.
     */
    skipDelayWhileScanning?: boolean
}

/**
 * Names a thing. One short string, hover only.
 *
 * Which one: a bare string is a Tooltip, however long. A label plus a second row, an avatar or a link is an
 * AstraHoverCard. Anything the user clicks, types or chooses inside is an AstraPopover.
 *
 * Needs one AstraTooltipProvider mounted once for the whole app (frontend/src/main.tsx); never add a second.
 * AstraTooltip only tags its child with `data-tooltip` attributes, the provider draws the panel.
 *
 * Timing belongs to the provider: 500ms before a tooltip opens, and a 200ms window after one closes in
 * which the next opens instantly, so scanning a toolbar doesn't cost a delay per icon. The delay itself is
 * not tunable per tooltip; only the skip can be turned off with `skipDelayWhileScanning`.
 *
 * Rules: hover only, so focus and touch never open it and it sets no `aria-describedby` — never let a fact
 * live only here. On an icon-only control, that means the name goes in `aria-label` on the trigger as well.
 * Never carry anything the user needs in order to finish the task: a field requirement, a format hint or a
 * password rule is hidden until hover, missing on touch, and gone the moment the pointer moves. Those go in
 * `helperText` or an AstraCallout. Never repeat the trigger's own visible label, and never say anything the
 * user has to act on — acting means moving the pointer, which dismisses the panel. Plain text, centred,
 * max 320px.
 *
 * The panel is `pointer-events-none`, so the pointer reaches whatever is underneath and nothing inside can
 * be clicked or selected. Text the user needs to copy goes in the page.
 *
 * Tooltips go on all of a group or none of it: one labelled icon in a toolbar of unlabelled ones reads as
 * a bug rather than a courtesy.
 *
 * The Astra/Tooltip docs page carries the full selection guide.
 */
export const AstraTooltip = ({
    children,
    showArrow = true,
    tooltipContent,
    hidden = false,
    noWrapText = false,
    side = 'top',
    skipDelayWhileScanning = true,
    shortcut,
}: Props) => {
    if (hidden || !tooltipContent) {
        return <>{children}</>
    }

    return (
        <div
            className="contents"
            data-tooltip-provider="hero-tooltip-wrapper"
            data-tooltip-placement={side}
            data-tooltip-no-wrap-text={noWrapText}
            data-tooltip-show-arrow={showArrow}
            data-tooltip-skip-delay={skipDelayWhileScanning}
            data-tooltip={tooltipContent}
            data-tooltip-shortcut={shortcut ? JSON.stringify(shortcut) : undefined}
        >
            {children}
        </div>
    )
}
